import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Campaign } from '../../services/campaign.service';

interface ScheduleData {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  recurringFrequency: string;
}

/**
 * Displays and manages the schedule details for a Campaign: formats start/end dates and times, exposes recurring frequency, and provides UI actions to toggle edit mode and navigate to the schedule editor.
 * @component
 * @selector app-campaign-schedule
 * @example
 *   <app-campaign-schedule [campaign]="sampleCampaign"></app-campaign-schedule>
 * @input {Campaign} campaign - Campaign object containing schedule_start_date, schedule_end_date, schedule_recurrence and id; used to build the displayed schedule data.
 * @output {none} none - This component does not emit any output events.
 *
 * Key methods:
 * - scheduleData (getter): Generates formatted startDate, startTime, endDate, endTime and recurringFrequency from the campaign data; handles missing or invalid dates gracefully.
 * - toggleEditMode(): Toggles the internal isEditMode flag to switch between view and edit UI states.
 * - onEditClick(): Navigates to the campaign editor's "Schedule" step and passes the campaign id as a query parameter.
 * - removeUnderscores(value: string | undefined): Utility to replace underscores with spaces in a string, safely handling undefined values.
 *
 * Lifecycle notes:
 * - No explicit Angular lifecycle hooks are implemented; the component relies on updates to the @Input() campaign and Angular change detection to refresh scheduleData when the input changes.
 */
@Component({
  selector: 'app-campaign-schedule',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './campaign-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-schedule.component.scss'],
})
export class CampaignScheduleComponent {
  @Input() campaign!: Campaign;

  constructor(private router: Router) {}

  // Generate schedule data from campaign data
  /**
   * Returns formatted schedule data extracted from the component's campaign or sensible defaults when campaign is missing.
   * @example
   * scheduleData()
   * {
   *   startDate: 'Jan 1, 2024',
   *   startTime: '09:00 AM',
   *   endDate: 'Jan 5, 2024',
   *   endTime: '05:00 PM',
   *   recurringFrequency: 'Daily'
   * }
   * @returns {ScheduleData} An object containing formatted startDate, startTime, endDate, endTime, and recurringFrequency.
   **/
  get scheduleData(): ScheduleData {
    if (!this.campaign) {
      return {
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        recurringFrequency: 'Daily',
      };
    }

    // Check if dates are null or empty before creating Date objects
    const hasStartDate =
      this.campaign.schedule_start_date && this.campaign.schedule_start_date.trim() !== '';
    const hasEndDate =
      this.campaign.schedule_end_date && this.campaign.schedule_end_date.trim() !== '';

    const startDate = hasStartDate ? new Date(this.campaign.schedule_start_date) : null;
    const endDate = hasEndDate ? new Date(this.campaign.schedule_end_date) : null;

    return {
      startDate:
        startDate && !isNaN(startDate.getTime())
          ? startDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '',
      startTime:
        startDate && !isNaN(startDate.getTime())
          ? startDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
          : '',
      endDate:
        endDate && !isNaN(endDate.getTime())
          ? endDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '',
      endTime:
        endDate && !isNaN(endDate.getTime())
          ? endDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
          : '',
      recurringFrequency: this.campaign.schedule_recurrence || 'Daily',
    };
  }

  isEditMode = false;

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
  }

  onEditClick(): void {
    this.router.navigate(['/campaign-editor/finish/', 'Schedule'], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  removeUnderscores(value: string | undefined): string {
    return value ? value.replace(/_/g, ' ') : '';
  }
}
